import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { format, parseISO, subWeeks, startOfToday, isBefore } from 'date-fns';
import { Employee, EmployeeWeekStatus, formatEmployeeName } from './config';
import { TimesheetBatch, TimesheetStatus, TimesheetView } from '../Timesheets/config';
import { useUrlFilters, ALL_TIMESHEETS_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import { useUserRole } from '../../utils/useUserRole';
import { notifyReadOnly, errorMessage } from '../../utils/supabaseErrors';
import SegmentedControl from '../../components/SegmentedControl';
import { useConfirmDialog } from '../../components/ConfirmDialog';
import { BaseTimesheetGrid, BaseTimesheetList } from '../../components/TimesheetTable';
import TableSkeleton from '../../components/TableSkeleton';
import ListPage from '../../components/ListPage';
import EmployeeList from './components/EmployeeList';
import WeekSelection from '../Timesheets/components/WeekSelection';
import TimesheetHeader from '../Timesheets/components/TimesheetHeader';
import DayPicker from '../Timesheets/components/DayPicker';
import DayView from '../Timesheets/components/DayView';
import { useTimesheetBatch } from '../Timesheets/useTimesheetBatch';
import { dateKey, dayLunch, dayTotals, groupEntries, isDateInWeek, weekStartKey, weekStats } from '../Timesheets/timesheetDerive';
import './styles.scss';

/** How far back the sidebar looks for weeks still in Draft. */
const OVERDUE_LOOKBACK_WEEKS = 26;

const VIEW_OPTIONS: { value: TimesheetView; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
  { value: 'list', label: 'List' }
];

/** The URL keeps the old 'grid' name for the week view. */
const tabToView = (tab: string): TimesheetView => (tab === 'day' ? 'day' : tab === 'list' ? 'list' : 'week');
const viewToTab = (view: TimesheetView): string => (view === 'week' ? 'grid' : view);

/**
 * Every employee's timesheets, read-only, with the manager's Approve and
 * Unapprove on the selected week. Same header, grid, day view and list as
 * the employee's page, from the same hook.
 */
const AllTimesheets: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();
  // The route is manager-only, but QA also passes it (read-only): the approve
  // controls need a manager who can actually write.
  const { isManager, isReadOnly } = useUserRole();
  const canApprove = isManager && !isReadOnly;
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const { filters: urlFilters, setFilter, setFilters: setUrlFilters } = useUrlFilters(ALL_TIMESHEETS_FILTER_DEFINITIONS);
  const selectedWeek = urlFilters.week;
  const view = tabToView(urlFilters.tab);
  const urlEmployeeId = urlFilters.employee;

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recentBatches, setRecentBatches] = useState<Pick<TimesheetBatch, 'employee_id' | 'week_ending_date' | 'status'>[]>([]);
  const [urlInitialized, setUrlInitialized] = useState(false);

  const api = useTimesheetBatch(selectedEmployee?.id ?? null, selectedWeek);
  const { batches, batch, entries, standardHours, defaultStart, lunch, loadingEntries } = api;

  const fetchEmployees = useCallback(async () => {
    if (!session?.user?.id) return;
    try {
      setLoading(true);
      const { data, error: err } = await supabase.from('employee_summary').select('*').order('first_name', { ascending: true });
      if (err) {
        console.error('Error fetching employees:', err);
        setError('Failed to load employees');
        return;
      }
      setEmployees(data || []);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, supabase]);

  // One query for every employee's recent weeks; the sidebar derives its dots
  // and overdue counts from it. Refreshed after approve/unapprove.
  const fetchRecentBatches = useCallback(async () => {
    if (!session?.user?.id) return;
    const since = format(subWeeks(startOfToday(), OVERDUE_LOOKBACK_WEEKS), 'yyyy-MM-dd');
    const { data, error: err } = await supabase
      .from('timesheet_batches')
      .select('employee_id, week_ending_date, status')
      .gte('week_ending_date', since);
    if (err) {
      console.error('Error fetching recent batches:', err);
      return;
    }
    setRecentBatches(data || []);
  }, [session?.user?.id, supabase]);

  useEffect(() => {
    fetchEmployees();
    fetchRecentBatches();
  }, [fetchEmployees, fetchRecentBatches]);

  // Initialize selected employee from URL (or default to first active) once employees load.
  useEffect(() => {
    if (urlInitialized || employees.length === 0) return;
    const urlEmployee = urlEmployeeId ? employees.find((emp) => emp.id === urlEmployeeId) : null;
    if (urlEmployee) {
      setSelectedEmployee(urlEmployee);
    } else {
      const first = employees.find((emp) => emp.is_active) || employees[0];
      setSelectedEmployee(first);
      setFilter('employee', first.id);
    }
    setUrlInitialized(true);
  }, [employees, urlEmployeeId, urlInitialized, setFilter]);

  // Default the week to the most recent batch when the URL has no valid week.
  useEffect(() => {
    if (batches.length === 0) return;
    if (selectedWeek && batches.some((b) => b.week_ending_date === selectedWeek)) return;
    setFilter('week', batches[0].week_ending_date);
  }, [batches, selectedWeek, setFilter]);

  const selectedDay = useMemo(() => {
    if (!selectedWeek) return '';
    if (urlFilters.day && isDateInWeek(urlFilters.day, selectedWeek)) return urlFilters.day;
    const today = dateKey(new Date());
    return isDateInWeek(today, selectedWeek) ? today : weekStartKey(selectedWeek);
  }, [urlFilters.day, selectedWeek]);

  const groups = useMemo(() => groupEntries(entries), [entries]);
  const stats = useMemo(() => weekStats(entries), [entries]);
  const totals = useMemo(() => dayTotals(entries), [entries]);
  const dayEntries = useMemo(() => entries.filter((e) => e.date === selectedDay), [entries, selectedDay]);
  const selectedDayLunch = useMemo(() => dayLunch(entries, selectedDay, lunch), [entries, selectedDay, lunch]);

  // Sidebar state per employee: the selected week's status, and how many
  // ended weeks are still Draft. Derived, so a refetch updates every row.
  const weekStatus = useMemo(() => {
    const today = startOfToday();
    const result: Record<string, EmployeeWeekStatus> = {};
    for (const b of recentBatches) {
      const row = result[b.employee_id] ?? { status: null, overdueCount: 0 };
      if (selectedWeek && b.week_ending_date === selectedWeek) row.status = b.status as TimesheetStatus;
      if (b.status === 'Draft' && isBefore(parseISO(b.week_ending_date), today)) row.overdueCount += 1;
      result[b.employee_id] = row;
    }
    return result;
  }, [recentBatches, selectedWeek]);

  const runBatchAction = useCallback(
    async (action: 'approve' | 'unapprove') => {
      if (!batch) return;
      try {
        setIsApproving(true);
        setActionError(null);
        if (action === 'approve') await api.approve();
        else await api.unapprove();
        await fetchRecentBatches();
      } catch (err) {
        console.error(`Error running ${action}:`, err);
        notifyReadOnly(err);
        setActionError(errorMessage(err, 'The change could not be saved.'));
      } finally {
        setIsApproving(false);
      }
    },
    [api, batch, fetchRecentBatches]
  );

  const handleUnapprove = useCallback(async () => {
    const ok = await confirm({
      title: 'Unapprove this week?',
      message: `${selectedEmployee ? formatEmployeeName(selectedEmployee) : 'The employee'} will be able to reopen and edit it again.`,
      confirmLabel: 'Unapprove',
      tone: 'warning'
    });
    if (ok) await runBatchAction('unapprove');
  }, [confirm, selectedEmployee, runBatchAction]);

  const handleEmployeeSelect = useCallback(
    (employee: Employee) => {
      if (selectedEmployee?.id !== employee.id) {
        setSelectedEmployee(employee);
        setActionError(null);
        setUrlFilters({ employee: employee.id, week: '', day: '' });
      }
    },
    [selectedEmployee?.id, setUrlFilters]
  );

  if (loading) {
    return (
      <ListPage title="All Timesheets" className="all-timesheets" loading skeleton={{ segmentCount: 0, filterCount: 0, showAction: false }}>
        <TableSkeleton compact rows={6} columns={4} showHeader />
      </ListPage>
    );
  }

  if (error) {
    return (
      <ListPage title="All Timesheets" className="all-timesheets">
        <div className="timesheets-page__error">
          <h3>Employee timesheets could not be loaded</h3>
          <p>{error}</p>
          <button type="button" className="pill-btn" onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </div>
      </ListPage>
    );
  }

  // The manager's lifecycle action lives in the identity strip beside the
  // status pill, as on Timesheets.
  const headerActions = canApprove ? (
    <>
      {batch?.status === 'Submitted' && (
        <button type="button" className="pill-btn pill-btn--primary" onClick={() => runBatchAction('approve')} disabled={isApproving}>
          {isApproving ? 'Approving…' : 'Approve week'}
        </button>
      )}
      {batch?.status === 'Approved' && (
        <button type="button" className="pill-btn pill-btn--ghost" onClick={handleUnapprove} disabled={isApproving}>
          {isApproving ? 'Working…' : 'Unapprove'}
        </button>
      )}
    </>
  ) : null;

  // The selected employee's week strip is the shell's second row, as on Timesheets.
  const weekStrip = selectedEmployee && batches.length > 0 ? (
    <WeekSelection
      selectedWeek={selectedWeek}
      onWeekSelect={(w) => setUrlFilters({ week: w, day: '' })}
      availableBatches={batches}
    />
  ) : undefined;

  return (
    <ListPage title="All Timesheets" className="all-timesheets" filters={weekStrip}>
      <div className="all-timesheets-content">
        <div className="employee-sidebar">
          <EmployeeList
            employees={employees}
            selectedEmployeeId={selectedEmployee?.id || null}
            onEmployeeSelect={handleEmployeeSelect}
            weekStatus={weekStatus}
          />
        </div>

        <div className="timesheet-viewer timesheets-page">
          {selectedEmployee ? (
            <>
              {batches.length === 0 && (
                <p className="ui-table__empty timesheets-page__empty">No timesheet weeks for {formatEmployeeName(selectedEmployee)}.</p>
              )}

              {batch && (
                <>
                  <TimesheetHeader
                    batch={batch}
                    stats={stats}
                    standardHours={standardHours}
                    employeeName={formatEmployeeName(selectedEmployee)}
                    actions={headerActions}
                  />
                  {actionError && (
                    <p className="field__error timesheets-page__lifecycle-error" role="alert">
                      {actionError}
                    </p>
                  )}

                  <div className="timesheets-page__toolbar">
                    <SegmentedControl<TimesheetView>
                      options={VIEW_OPTIONS}
                      value={view}
                      onChange={(v) => v && setFilter('tab', viewToTab(v))}
                      size="sm"
                      aria-label="Timesheet view"
                      className="timesheets-page__view"
                    />
                  </div>

                  {view === 'day' && (
                    <DayPicker
                      weekEndingDate={batch.week_ending_date}
                      selectedDate={selectedDay}
                      onSelect={(d) => setFilter('day', d)}
                      totals={totals}
                      standardHours={standardHours}
                    />
                  )}

                  {loadingEntries ? (
                    <TableSkeleton rows={4} columns={9} showHeader rowHeight="40px" />
                  ) : view === 'week' ? (
                    <BaseTimesheetGrid groups={groups} weekEndingDate={batch.week_ending_date} isReadOnly standardHoursPerDay={standardHours} />
                  ) : view === 'day' ? (
                    <DayView
                      date={selectedDay}
                      entries={dayEntries}
                      isReadOnly
                      standardHours={standardHours}
                      defaultStart={defaultStart}
                      lunch={selectedDayLunch.lunch}
                      usualLunch={lunch}
                      lunchIsCustom={selectedDayLunch.isCustom}
                      lunchSaveState={() => 'idle'}
                      onLunchChange={() => undefined}
                      saveState={() => 'idle'}
                      onPatch={() => undefined}
                      onCreate={async () => undefined}
                      onEdit={() => undefined}
                      onDuplicate={() => undefined}
                      onDelete={() => undefined}
                      addSeed={null}
                      onAddSeedConsumed={() => undefined}
                    />
                  ) : (
                    <BaseTimesheetList entries={entries} />
                  )}
                </>
              )}
            </>
          ) : (
            <p className="ui-table__empty timesheets-page__empty">Choose an employee from the list to view their timesheets.</p>
          )}
        </div>
      </div>
      {confirmDialog}
    </ListPage>
  );
};

export default AllTimesheets;
